import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    useTheme,
    Tooltip,
    InputAdornment,
    Avatar,
    Skeleton,
    Chip,
    Paper,
    Stack,
    Snackbar,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search,
    HowToVote,
    Person,
    ArrowBack,
    NavigateNext,
    HolidayVillage
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import api from '../../api/axios';

interface MasterVotingBooth {
    id: number;
    name: string;
    booth_id: string; // The specific booth ID string e.g., "123"
    citizens_count: number;
}

const MasterVillageDetail = () => {
    const { masterConstituencyId, masterVillageId } = useParams<{ masterConstituencyId: string, masterVillageId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const theme = useTheme();

    // State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState<MasterVotingBooth | null>(null);
    const [formData, setFormData] = useState({ name: '', booth_id: '' });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [search, setSearch] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<MasterVotingBooth | null>(null);

    // Fetch Village Details
    const { data: village, isLoading: loadingVillage } = useQuery({
        queryKey: ['master-village', masterVillageId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/master-villages/${masterVillageId}`);
            return res.data?.data || res.data;
        },
        enabled: !!masterVillageId,
    });

    // Fetch Voting Booths
    const { data: boothsData, isLoading: loadingBooths } = useQuery({
        queryKey: ['master-voting-booths', masterVillageId, search],
        queryFn: async () => {
            const params: any = { master_village_id: masterVillageId, per_page: 100 };
            if (search) params.search = search;
            const res = await api.get('/superadmin/master-voting-booths', { params });
            return res.data;
        },
        enabled: !!masterVillageId,
        placeholderData: keepPreviousData,
    });

    const booths = boothsData?.data?.data || boothsData?.data || [];

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: any) => api.post('/superadmin/master-voting-booths', {
            ...data,
            master_constituency_id: Number(masterConstituencyId),
            master_village_id: Number(masterVillageId),
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-voting-booths'] });
            queryClient.invalidateQueries({ queryKey: ['master-village'] });
            handleClose();
            showSnackbar('Voting booth created successfully', 'success');
        },
        onError: (err: any) => {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFormErrors(err.response.data.errors);
            } else {
                showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
            }
        }
    });

    // Update Mutation
    const updateMutation = useMutation({
        mutationFn: async (data: any) => api.put(`/superadmin/master-voting-booths/${data.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-voting-booths'] });
            handleClose();
            showSnackbar('Voting booth updated successfully', 'success');
        },
        onError: (err: any) => {
            showSnackbar(err.response?.data?.message || 'Failed to update voting booth', 'error');
        }
    });

    // Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/master-voting-booths/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-voting-booths'] });
            queryClient.invalidateQueries({ queryKey: ['master-village'] });
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            showSnackbar('Voting booth deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete voting booth', 'error')
    });

    const handleOpen = (item?: MasterVotingBooth) => {
        if (item) {
            setEditingItem(item);
            setFormData({ name: item.name, booth_id: item.booth_id });
        } else {
            setEditingItem(null);
            setFormData({ name: '', booth_id: '' });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ name: '', booth_id: '' });
    };

    const handleSubmit = () => {
        if (!formData.name) {
            setFormErrors({ ...formErrors, name: 'Name is required' });
            return;
        }
        if (!formData.booth_id) {
            setFormErrors({ ...formErrors, booth_id: 'Booth ID is required' });
            return;
        }

        if (editingItem) {
            updateMutation.mutate({ ...formData, id: editingItem.id });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleDeleteClick = (item: MasterVotingBooth) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete) {
            deleteMutation.mutate(itemToDelete.id);
        }
    };

    const isLoading = loadingVillage || loadingBooths;
    const totalBooths = booths.length;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                    <Link component={RouterLink} color="inherit" to="/super-admin/master-constituencies" sx={{ display: 'flex', alignItems: 'center' }}>
                        Master Constituencies
                    </Link>
                    <Link component={RouterLink} color="inherit" to={`/super-admin/master-constituencies/${masterConstituencyId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        {village?.constituency?.name || 'Constituency'}
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        {village?.name || 'Loading...'}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', // Green gradient for Village
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(`/super-admin/master-constituencies/${masterConstituencyId}`)}
                            sx={{ color: 'white' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        {village?.name || <Skeleton width={200} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, ml: 7 }}>
                        Manage voting booths within this village
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 2, ml: 7 }}>
                        <Chip
                            label={`${totalBooths} Booths`}
                            color="default"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                        <Chip
                            label={`${village?.citizens_count || 0} Citizens`}
                            color="default"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                    </Stack>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpen()}
                    sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        bgcolor: 'white',
                        color: '#059669',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                    }}
                >
                    Add Voting Booth
                </Button>
            </Paper>

            {/* Controls Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Voting Booths
                </Typography>
                <TextField
                    placeholder="Search booths..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {isLoading ? (
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: 200, borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={44} height={44} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="60%" />
                                            <Skeleton variant="text" width="40%" />
                                        </Box>
                                    </Box>
                                    <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (booths as MasterVotingBooth[]).length > 0 ? (
                    (booths as MasterVotingBooth[]).map((item) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    }
                                }}
                                onClick={() => navigate(`/super-admin/master-constituencies/${masterConstituencyId}/villages/${masterVillageId}/voting-booths/${item.id}`)}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'rgba(79, 70, 229, 0.1)', // Indigo/Blue tint
                                                color: '#4f46e5',
                                                width: 44,
                                                height: 44,
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem'
                                            }}
                                        >
                                            {item.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpen(item);
                                                    }}
                                                    sx={{ color: theme.palette.warning.main }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(item);
                                                    }}
                                                    sx={{ color: theme.palette.error.main }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.2 }}>
                                        {item.name}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        icon={<HowToVote style={{ fontSize: 16 }} />}
                                        label={`Booth #${item.booth_id}`}
                                        sx={{ mb: 2, bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 'medium' }}
                                    />

                                    <Box sx={{ mt: 'auto' }}>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Person fontSize="small" color="success" />
                                                <Typography variant="caption">{item.citizens_count || 0} Citizens</Typography>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ mb: 2, opacity: 0.5 }}>
                                <HowToVote sx={{ fontSize: 60, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {search ? `No booths found matching "${search}"` : "No voting booths in this village yet"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Add voting booths to assign citizens
                            </Typography>
                            {!search && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => handleOpen()}
                                >
                                    Add First Voting Booth
                                </Button>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingItem ? 'Edit Voting Booth' : 'Add New Voting Booth'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            label="Booth Name"
                            placeholder="e.g. Primary School, Room 1"
                            fullWidth
                            variant="outlined"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                            disabled={createMutation.isPending || updateMutation.isPending}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><HowToVote color="action" /></InputAdornment>,
                            }}
                        />
                        <TextField
                            label="Booth ID"
                            placeholder="e.g. 12"
                            fullWidth
                            variant="outlined"
                            value={formData.booth_id}
                            onChange={(e) => setFormData({ ...formData, booth_id: e.target.value })}
                            error={!!formErrors.booth_id}
                            helperText={formErrors.booth_id}
                            disabled={createMutation.isPending || updateMutation.isPending}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} sx={{ borderRadius: 2, textTransform: 'none' }} size="large">Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                        sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                        size="large"
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Voting Booth'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>"{itemToDelete?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Warning: This will also delete all associated citizens!
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MasterVillageDetail;
